import {
  Card,
  Heading,
  Text,
  HStack,
  VStack,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal,
  Separator,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { MoreVertical, Eye, Trash2 } from 'lucide-react'
import type { Residence } from '../types'
import { PropertyStats } from './PropertyStats'

interface PropertyCardProps {
  property: Residence
  isSelected?: boolean
  onClick?: (id: string) => void
  onDelete?: (id: string) => void
}

export function PropertyCard({
  property,
  isSelected = false,
  onClick,
  onDelete,
}: PropertyCardProps) {
  const { t } = useTranslation()

  return (
    <Card.Root
      data-property-card
      cursor="pointer"
      onClick={() => onClick?.(property.id)}
      borderWidth="2px"
      borderColor={isSelected ? 'brand.800' : 'transparent'}
      bg={isSelected ? 'brand.50' : 'white'}
      _hover={{
        borderColor: isSelected ? 'brand.900' : 'gray.300',
        bg: isSelected ? 'brand.100' : 'gray.50',
      }}
      transition="all 0.2s"
    >
      <Card.Header pb={2}>
        <HStack justify="space-between" align="start">
          <VStack align="stretch" gap={1}>
            <Heading size="md" color={isSelected ? 'brand.700' : 'inherit'}>
              {property.name}
            </Heading>
          </VStack>

          {/* Actions menu - aligned top right */}
          <div onClick={(e) => e.stopPropagation()}>
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label={t('common.actions')}
                >
                  <MoreVertical size={16} />
                </IconButton>
              </MenuTrigger>
              <Portal>
                <MenuPositioner>
                  <MenuContent minW="180px">
                    <MenuItem
                      value="details"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClick?.(property.id)
                      }}
                    >
                      <HStack gap={2}>
                        <Eye size={16} />
                        <Text>{t('common.details')}</Text>
                      </HStack>
                    </MenuItem>
                    {onDelete && (
                      <MenuItem
                        value="delete"
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(property.id)
                        }}
                        color="red.600"
                        _hover={{ bg: 'red.50' }}
                      >
                        <HStack gap={2}>
                          <Trash2 size={16} />
                          <Text>{t('common.delete')}</Text>
                        </HStack>
                      </MenuItem>
                    )}
                  </MenuContent>
                </MenuPositioner>
              </Portal>
            </MenuRoot>
          </div>
        </HStack>
      </Card.Header>

      <Card.Body pt={2}>
        <VStack align="stretch" gap={3}>
          <VStack align="stretch" gap={1}>
            <Text fontSize="sm" color="gray.600">
              {property.postal_code} {property.city}
            </Text>
            {property.country && (
              <Text fontSize="sm" color="gray.600">
                {property.country}
              </Text>
            )}
          </VStack>

          <Separator my={2} />

          <PropertyStats
            residenceId={property.id}
            onUnitsClick={() => console.log('Navigate to units')}
            onOwnersClick={() => console.log('Navigate to owners')}
          />
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}
